import { supabase } from './supabase';

/*
  THE NEW ITEM CATALOG, as the screens read it. Job 37, 11 October 2026.

  **Found by the owner**: the Item Catalog screen and count entry still read the
  old `items` table, which holds only OnHand Testing's 5,000 items, so MedEx saw
  nothing. Everything here reads the new catalog instead:

    org_catalog      one row per company item, with its main ClaimSoft and
                     Account Edge numbers, vendor and newest price. A view, and
                     it runs as the person asking, so a company sees its own.
    catalog_items    every vendor item, 354,063, shared by every company.

  **His answer of 11 October**: the screen opens on the company's own items, and
  a switch or a search reaches every vendor item.

  Cost each is only what the newest price book says: where the book does not
  say whether a price is for one or for a case, it is left empty, not guessed.
*/

export const PAGE = 50;

/*
  **The company's own items, a page at a time**, through `org_catalog_page()`
  in the database. It finds the matches first, using the indexes, and then
  builds only the page; reading the `org_catalog` view directly works out every
  item and its price before it can sort, 8 to 9 seconds a page on this
  database. Measured on 11 October 2026: a page in about 0.15 seconds and a
  search in 0.05 to 0.3. Ordered by the item's main ClaimSoft number (or its
  vendor part number), which the database keeps on the item as it changes.
*/
export async function loadOrgPage({ search = '', vendorId = '', from = 0, size = PAGE } = {}) {
  const { data, error } = await supabase.rpc('org_catalog_page', {
    p_search: search.trim() || null, p_vendor_id: vendorId || null, p_from: from, p_size: size,
  });
  const rows = data || [];
  return { rows, total: rows.length ? Number(rows[0].total) : 0, error };
}

/*
  **Every vendor item**, a page at a time, through `vendor_catalog_page()`.
  Read directly, the vendor catalog's sharing rule asks whether the person is
  an admin again for each of its 354,063 rows, and the page timed out after 8
  seconds (seen in the preview, 11 October 2026). The function asks once, uses
  the search indexes, and marks the items the company has. Measured that day:
  a page in about 0.15 seconds, most searches under 0.4, "walker" (4,254
  matches) about 1.1. The total with no search is the database's estimate.
*/
export async function loadVendorPage({ search = '', vendorId = '', from = 0, size = PAGE } = {}) {
  const { data, error } = await supabase.rpc('vendor_catalog_page', {
    p_search: search.trim() || null, p_vendor_id: vendorId || null, p_from: from, p_size: size,
  });
  if (error) return { rows: [], total: 0, error };
  const rows = data || [];
  return {
    rows: rows.map(r => ({
      catalog_item_id: r.catalog_item_id, oh_number: r.oh_number, vendor_part_number: r.vendor_part_number,
      description: r.description, vendor: r.vendor, status: r.status, isMine: r.is_ours,
    })),
    total: rows.length ? Number(rows[0].total) : 0, error: null,
  };
}

/* The vendors, for the filter. */
export async function loadVendors() {
  const { data, error } = await supabase.from('vendors').select('id, name').order('name');
  return { vendors: data || [], error };
}

/* How many items the company has, and how many carry each system's main
   number. Counted from the tables themselves, not the view, which would work
   out every price to count them. */
export async function loadOrgCounts() {
  const [all, cs, ae] = await Promise.all([
    supabase.from('org_items').select('id', { count: 'exact', head: true }),
    supabase.from('org_item_numbers').select('id', { count: 'exact', head: true }).eq('system', 'claimsoft').eq('is_main', true),
    supabase.from('org_item_numbers').select('id', { count: 'exact', head: true }).eq('system', 'account_edge').eq('is_main', true),
  ]);
  const err = all.error || cs.error || ae.error;
  return err ? { error: err } : { items: all.count || 0, claimsoft: cs.count || 0, accountEdge: ae.count || 0, error: null };
}

/*
  **Everything about one item**: the vendor's record, packaging, barcodes,
  every ClaimSoft and Account Edge number the company has on it (main, Medicare
  and others), the newest price for each unit, and any conversion its ClaimSoft
  numbers are in.
*/
export async function loadItemDetail(catalogItemId) {
  const { data: item, error } = await supabase.from('catalog_items')
    .select('id, oh_number, vendor_part_number, vendor_description, our_description, manufacturer, brand, category, product_family, size, side, gender, colour, list_price, status, discontinued_on, needs_review, review_reason, vendor:vendors(name)')
    .eq('id', catalogItemId).maybeSingle();
  if (error || !item) return { error: error || { message: 'That item was not found.' } };
  const [packs, codes, own, prices] = await Promise.all([
    supabase.from('catalog_item_packs').select('id, uom, items_per_uom, is_purchase_default').eq('catalog_item_id', catalogItemId).order('items_per_uom'),
    supabase.from('catalog_item_barcodes').select('code, kind').eq('catalog_item_id', catalogItemId).order('code'),
    supabase.from('org_items').select('*, numbers:org_item_numbers(id, system, number, is_main, reason, match_method, match_status, confirmed_at)').eq('catalog_item_id', catalogItemId).maybeSingle(),
    supabase.from('catalog_current_prices').select('purchase_uom, uom_key, cost_per_uom, cost_per_item, book_label, priced_on').eq('catalog_item_id', catalogItemId).order('priced_on', { ascending: false }),
  ]);
  const numbers = (own.data && own.data.numbers) || [];
  const csNumbers = numbers.filter(n => n.system === 'claimsoft').map(n => n.number);
  let conversions = [];
  if (csNumbers.length) {
    const keys = csNumbers.map(n => n.trim().toUpperCase());
    const { data: conv } = await supabase.from('product_conversions')
      .select('old_number, new_number').or('old_key.in.(' + keys.map(k => '"' + k.replace(/"/g, '\\"') + '"').join(',') + '),new_key.in.(' + keys.map(k => '"' + k.replace(/"/g, '\\"') + '"').join(',') + ')');
    conversions = conv || [];
  }
  return {
    item, packs: packs.data || [], barcodes: codes.data || [], orgItem: own.data || null,
    numbers, prices: prices.data || [], conversions, error: packs.error || codes.error || own.error || prices.error || null,
  };
}

/*
  **For count entry: the company's items matching what was typed.** Fifteen is
  what the list under the box has room for.
*/
export async function searchForCount(text, limit = 15) {
  const s = String(text || '').trim();
  if (s.length < 2) return { items: [], error: null };
  const { rows, error } = await loadOrgPage({ search: s, size: limit });
  return { items: rows.map(asCountItem), error };
}

/* A barcode, scanned, to the company's item it is on. Exact, ignoring case and
   spaces at the ends, as the barcode table keys it. */
export async function findByBarcodeForCount(code) {
  const key = String(code || '').trim().toUpperCase();
  if (!key) return { item: null, error: null };
  const { data: hit, error } = await supabase.from('catalog_item_barcodes').select('catalog_item_id').eq('code_key', key).maybeSingle();
  if (error || !hit) return { item: null, error };
  const { data, error: e2 } = await supabase.from('org_catalog')
    .select('org_item_id, item_number, description, vendor, claimsoft_number, account_edge_number')
    .eq('catalog_item_id', hit.catalog_item_id).maybeSingle();
  return { item: data ? asCountItem(data) : null, error: e2 };
}

/* The shape count entry has always used for a catalog item, so the rest of it
   does not change: id, item_number, description, vendor. `id` is the company
   item now; `source` says which table it came from. */
export function asCountItem(r) {
  return { id: r.org_item_id, org_item_id: r.org_item_id, item_number: r.item_number, description: r.description, vendor: r.vendor, source: 'org_catalog' };
}

/*
  **Which column a counted line points through.** A company item from the new
  catalog goes in `org_item_id`; an item from the old table, which only OnHand
  Testing still has, keeps going in `item_catalog_id`, so its 3,250 old lines and
  anything it counts next work exactly as before.
*/
export function lineLink(item) {
  return item && item.source === 'org_catalog'
    ? { org_item_id: item.id, item_catalog_id: null }
    : { item_catalog_id: item ? item.id : null };
}

/* Cost each for some company items, for a count sheet's value. */
export async function costsForOrgItems(ids) {
  const want = [...new Set(ids.filter(Boolean))];
  const out = new Map();
  for (let i = 0; i < want.length; i += 150) {
    const { data, error } = await supabase.from('org_catalog').select('org_item_id, cost_each').in('org_item_id', want.slice(i, i + 150));
    if (error) return { costs: out, error };
    for (const r of data || []) out.set(r.org_item_id, r.cost_each == null ? null : Number(r.cost_each));
  }
  return { costs: out, error: null };
}
